import { useEffect, useState } from "react";
import CTAButton from "./CTAButton";

function useCountdown() {
  // Fixed 3h 27m countdown that persists per session via sessionStorage
  const INITIAL_SECONDS = 3 * 3600 + 27 * 60; // 3 jam 27 menit
  const [s, setS] = useState(() => {
    const stored = sessionStorage.getItem("kp_countdown_end");
    if (stored) {
      const remaining = Math.floor((Number(stored) - Date.now()) / 1000);
      return Math.max(remaining, 0);
    }
    const endTime = Date.now() + INITIAL_SECONDS * 1000;
    sessionStorage.setItem("kp_countdown_end", String(endTime));
    return INITIAL_SECONDS;
  });
  useEffect(() => {
    const i = setInterval(() => {
      const stored = sessionStorage.getItem("kp_countdown_end");
      if (!stored) return;
      const remaining = Math.floor((Number(stored) - Date.now()) / 1000);
      setS(Math.max(remaining, 0));
    }, 1000);
    return () => clearInterval(i);
  }, []);
  const h = String(Math.floor(s / 3600)).padStart(2, "0");
  const m = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  const sec = String(s % 60).padStart(2, "0");
  return { h, m, s: sec };
}

export default function OfferSection({
  content = {},
  productImage,
  productName,
  productDesc,
  price,
  comparePrice,
  hasComparePrice,
  formatRupiah,
  onCheckout,
  canCheckout,
  onWhatsapp,
  whatsappEnabled,
}) {
  const { h, m, s } = useCountdown();
  const title = content.title || "Promo Spesial Hari Ini";
  const badge = content.badge || "Khusus Hari Ini — Diskon 20%";
  const desc = content.desc || "";
  const couponCode = content.coupon_code || "";
  const couponLabel = content.coupon_label || "";
  const bonusItems = content.bonus_items || [];

  return (
    <section className="py-20 lg:py-28 bg-gradient-hero text-white relative overflow-hidden">
      <div className="absolute -top-20 -left-20 h-80 w-80 rounded-full bg-white/10 blur-3xl" />
      <div className="absolute -bottom-20 -right-20 h-80 w-80 rounded-full bg-[oklch(0.62_0.24_22/0.3)] blur-3xl" />

      <div className="relative mx-auto max-w-6xl px-5">
        <div className="text-center">
          <span className="inline-block rounded-full bg-white text-primary px-4 py-1.5 text-xs font-bold uppercase tracking-widest">
            {badge}
          </span>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold text-balance">
            {title}
          </h2>
        </div>

        <div className="mt-10 grid lg:grid-cols-2 gap-8 items-center">
          <div className="rounded-[2rem] bg-white text-foreground p-8 shadow-luxury">
            <div className="flex items-center gap-3">
              <img src={productImage} alt={productName} className="h-24 w-24 rounded-2xl object-cover" />
              <div>
                <div className="font-display text-xl font-bold">{productName}</div>
                <div className="text-xs text-muted-foreground">{productDesc}</div>
              </div>
            </div>

            <div className="mt-6 flex items-end gap-3">
              {hasComparePrice && (
                <div className="text-muted-foreground line-through text-lg">{formatRupiah(comparePrice)}</div>
              )}
              <div className="font-display text-5xl font-extrabold text-primary">
                {price ? formatRupiah(price) : "Harga Promo"}
              </div>
            </div>
            {hasComparePrice && (
              <div className="mt-1 inline-block bg-[oklch(0.52_0.22_25/0.1)] text-primary font-bold px-3 py-1 rounded-full text-sm">
                HEMAT {formatRupiah(comparePrice - price)}
              </div>
            )}

            {bonusItems.length > 0 && (
              <ul className="mt-6 space-y-2 text-sm">
                {bonusItems.map((item, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <span className="text-green-600">✓</span> {item}
                  </li>
                ))}
              </ul>
            )}

            {couponCode && (
              <div className="mt-6 rounded-2xl border-2 border-dashed border-[oklch(0.52_0.22_25/0.4)] bg-[var(--kp-accent)]/50 p-4 text-center">
                <div className="text-xs uppercase tracking-widest text-muted-foreground">🎁 Kode Kupon</div>
                <div className="font-display font-extrabold text-2xl text-primary mt-1">{couponCode}</div>
                {couponLabel && <div className="text-xs text-foreground/80">{couponLabel}</div>}
              </div>
            )}

            <div className="mt-6">
              <CTAButton size="xl" fullWidth pulse onClick={onCheckout} disabled={!canCheckout}>
                🔥 Claim Diskon & Checkout
              </CTAButton>
            </div>
          </div>

          <div className="text-center lg:text-left">
            <div className="text-sm uppercase tracking-widest text-white/80">Promo Berakhir Dalam</div>
            <div className="mt-3 flex gap-3 justify-center lg:justify-start">
              {[{ l: "Jam", v: h }, { l: "Menit", v: m }, { l: "Detik", v: s }].map((u) => (
                <div key={u.l} className="rounded-2xl bg-white/15 backdrop-blur-md border border-white/30 p-4 min-w-[80px]">
                  <div className="font-display text-4xl font-extrabold tabular-nums">{u.v}</div>
                  <div className="text-[10px] uppercase tracking-widest text-white/80">{u.l}</div>
                </div>
              ))}
            </div>
            {desc && (
              <p className="mt-6 text-white/90 max-w-md">
                {desc}
              </p>
            )}
            {whatsappEnabled && (
              <div className="mt-6">
                <CTAButton size="lg" variant="ghost-light" onClick={onWhatsapp}>
                  Ambil Promo Hari Ini
                </CTAButton>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
